import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import type { BlockVisualization } from 'webapp/enums/enums';
import { BillingFeature } from 'webapp/enums/enums';

import definite from 'webapp/helpers/definite';
import setField from 'webapp/helpers/set-field';
import BlocktypePickerComponent from 'webapp/components/blocktype-picker';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupCommonDimensionSelectorComponent from 'webapp/components/custom-query/setup/common/dimension-selector/index';
import CustomQuerySetupCommonPreviewChartComponent from 'webapp/components/custom-query/setup/common/preview-chart';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectBoardWithCreateNewOptionComponent from 'webapp/components/form/select-board-with-create-new-option';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { BoardRecord } from 'webapp/models';
import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';

import { StepId } from 'webapp/services/wizard';

type Args = Record<string, never>;

export default class WizardCreateBlockComponent extends Component<Args> {
	@service('store') declare storeService: Service.Store;
	@service('billing') declare billingService: Service.Billing;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked submitInProgress = false;

	listClass: string | null = null;

	blockName = '';
	boardName: string | null = null;

	selectedBoardId: string | null = null;

	@tracked form:
		| CustomMetricSetupFormSheet
		| CustomMetricSetupFormPrebuilt
		| CustomMetricSetupFormApiMetricBuilder
		| null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.blockName = this.customQuery?.metricSetup.metric?.name || '';
		this.boardName = this.hasBoards ? null : 'My first dashboard';

		// we don't want this class to change
		const classes = [];

		classes.push(this.hasBoards ? 'wide' : 'narrow');

		if (this.hasPreview) {
			classes.push('has-preview');
		}

		this.listClass = classes.join(' ');

		const customQuery = this.customQuery;
		if (this.hasPreview && customQuery) {
			// must create form to handle preview
			customQuery.builderId = String(this.customQuery.metricSetup.spaceAccessId);

			const form = this.mmCustomQueriesService.createSetupFormForCustomQuery(customQuery);

			// we don't have the form to read values from, so we use settings
			form.prepareSettingsFn = function () {
				return form.query.settings;
			};

			next(() => {
				// set in next to make sure form already has boardState
				this.form = form;
				form.scheduleQueryPreview();
			});
		}
	}

	get metric() {
		return this.wizardService.createdCustomMetric;
	}

	get customQuery() {
		return this.wizardService.createdCustomQuery;
	}

	get isAttributed() {
		return this.customQuery?.metricSetup.isOutputMultipleAttributes;
	}

	get hasPreview() {
		return this.wizardService.selectedSourceType?.isSheets;
	}

	get hasBoards() {
		return this.mmBoardsService.normalBoardModels.length > 0;
	}

	get blockType() {
		return this.form?.previewBlock.visualization;
	}

	createBlock(boardId: number) {
		const board = this.mmBoardsService.findModel(String(boardId)),
			dateRange = this.form?.previewBlockState?.limitationsCM?.dateRange?.formula;

		const block = this.mmBlocksService.createNewModel({
			name: this.blockName,
			boardId,
			visualization: this.blockType,
			fromMetricSetup:
				this.customQuery?.metricSetup.metricSetupWithoutFiltersData ?? undefined,
			fromMetricDateRange: dateRange,
			order: board?.blocks.length, // put it at the end
			// don't set datawall position so it can take up the space when opened
		});

		block
			.save()
			.then((blockRecord) => {
				const boardRecord = this.storeService.peekRecord(
					'board',
					blockRecord.boardId,
				) as BoardRecord;

				if (boardRecord) {
					this.navigationService.goToBoardsAndOpenPreview(boardRecord);
				}
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	changeSelectedBoardId(boardId: string) {
		this.selectedBoardId = boardId;
	}

	@action
	changeBlockName(): void {
		const newName = this.blockName.trim();
		const previewBlock = this.form?.previewBlock;
		if (newName && previewBlock && newName !== previewBlock.name) {
			previewBlock.name = newName;
		}
	}

	@action
	changeBlockType(blockType: BlockVisualization) {
		const previewBlock = this.form?.previewBlock;
		if (previewBlock) {
			void previewBlock.blockSetup.changeVisualization(blockType);
		}
	}

	@action
	createNewMetric() {
		this.wizardService.createdCustomQuery = null;
		this.wizardService.goToStep(StepId.CREATE_METRIC);
	}

	@action
	next() {
		const selectedBoardId = this.selectedBoardId;

		if (!this.hasBoards || !selectedBoardId) {
			// new board!
			if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
				this.submitInProgress = true;

				const board = this.mmBoardsService.createNewModel({
					primaryView: 7,
					name: this.boardName ?? undefined, // undefined lets the model default name apply
				});
				board
					.save()
					.then(
						(board) => {
							next(this, function () {
								this.createBlock(+board.id!);
							});
						},
						(errorData) => {
							this.submitInProgress = false;
							throw errorData;
						},
					)
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			}
		} else {
			this.submitInProgress = true;
			this.createBlock(+selectedBoardId);
		}
	}

	@action
	back() {
		this.wizardService.goToStep(StepId.CREATE_METRIC);
	}
	<template>
		<div class="wizard-title">

			<div class="h">
				<h2 class="check">
					<CommonUiIconComponent @name="CheckCircleFill" @size="xl" />
					You successfully created your metric!
				</h2>
				<p>Wow, you're doing great! Now put it on a dashboard.</p>
			</div>

		</div>

		<div class="content">

			<div class="list {{this.listClass}}">

				{{#if this.form}}

					{{#if this.hasPreview}}
						<div class="block-preview">

							<CustomQuerySetupCommonPreviewChartComponent @form={{this.form}} />

							{{#if this.form.availableDimensions}}
								<CustomQuerySetupCommonDimensionSelectorComponent
									@form={{this.form}}
									@theme="dark"
								/>
							{{/if}}

						</div>
					{{/if}}

					<div class="block-data">
						<div class="row">
							<label>Name your datablock</label>
							<FormInputComponent
								name="blockName"
								@value={{this.blockName}}
								@onEmpty={{setField target=this property="blockName"}}
								@focusOut={{this.changeBlockName}}
								maxlength="50"
								disabled={{this.submitInProgress}}
							/>
						</div>
						<div class="row">
							<label>How would you like to visualize it?</label>
							<BlocktypePickerComponent
								@blockType={{definite this.blockType}}
								@isAttributed={{this.isAttributed}}
								@onChangeBlockType={{this.changeBlockType}}
							/>
						</div>
						<div class="row">

							{{#if this.hasBoards}}
								<label>Where would you like to add this metric to?</label>
								<FormSelectBoardWithCreateNewOptionComponent
									@changeSelectedBoardId={{this.changeSelectedBoardId}}
								/>
							{{else}}
								<label>How would you like to name your dashboard?</label>
								<FormInputComponent
									name="boardName"
									@value={{this.boardName}}
									@onEmpty={{setField target=this property="boardName"}}
									maxlength="50"
									disabled={{this.submitInProgress}}
								/>
							{{/if}}

						</div>
					</div>
				{{/if}}

			</div>

			<div class="wizard-controls smart-center-pos">
				<div class="container">

					<SubmitButtonComponent
						@text="New metric"
						@extraClass="next"
						@size="big"
						@theme="white"
						@noDelay={{true}}
						@isDisabled={{this.submitInProgress}}
						@onTrigger={{this.createNewMetric}}
					/>

					<SubmitButtonComponent
						@text="Finish"
						@extraClass="next"
						@size="big"
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.next}}
					/>

					<a role="button" class="btn back" {{on "click" this.back}}>
						<CommonUiIconComponent @name="ArrowLeft" @size="xs" />
						Go back
					</a>

				</div>
			</div>

		</div>
	</template>
}
